import LayersClearIcon from "@mui/icons-material/LayersClear";
import { MenuItem } from "@mui/material";
import { MoreVerticalIcon } from "lucide-react";

import {
  ViewTagsAndMetadata,
  DownloadFile,
  OpenFileInNewTab,
} from "@/Files/File/FileHeader/controls";
import { useAnalytics } from "@/providers";
import { RobotoIconButton, RobotoTooltip } from "@/shared/components";
import { FileDetailsDialog } from "@/shared/components/FileDetails/FileDetailsDialog";
import { Menu } from "@/shared/components/Menu";
import { FileRecord } from "@/shared/domain/files";
import { usePopupState } from "@/shared/hooks";
import { actions, useVizDispatch } from "@/shared/state/visualization";

interface HeaderActionsMenuProps {
  /** Single file record to display actions for. */
  fileRecord?: FileRecord;

  /** Optional className for styling */
  className?: string;
}

export function HeaderActionsMenu({
  fileRecord,
  className,
}: HeaderActionsMenuProps) {
  const dispatch = useVizDispatch();
  const { trackEvent } = useAnalytics();

  const dropdownMenu = usePopupState();
  const tagMetadataPopup = usePopupState();

  const menuItems = getMenuItemKeys(!!fileRecord);

  const handleClearPanels = () => {
    trackEvent("ClearPanelsClicked");
    dispatch(actions.removeAllPanels());
    dropdownMenu.close();
  };

  const handleViewFileDetails = () => {
    tagMetadataPopup.open();
    dropdownMenu.close();
  };

  return (
    <div className={className}>
      <RobotoTooltip title="More actions">
        <div>
          <RobotoIconButton
            eventName={"MoreActionsClicked"}
            onClick={(event) => {
              dropdownMenu.open({ anchorEl: event.currentTarget });
            }}
            sx={{ padding: "0.5rem" }}
            size="small"
          >
            <MoreVerticalIcon
              style={{
                width: "1.25rem",
                height: "1.25rem",
              }}
            />
          </RobotoIconButton>
        </div>
      </RobotoTooltip>

      <Menu
        anchorEl={dropdownMenu.anchor}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        open={dropdownMenu.isOpen}
        onClose={dropdownMenu.close}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        {menuItems.map((key) => {
          switch (key) {
            case "clear-panels":
              return (
                <MenuItem key={key} onClick={handleClearPanels}>
                  <LayersClearIcon
                    style={{
                      width: "1rem",
                      height: "1rem",
                      marginRight: "0.75rem",
                    }}
                  />
                  Clear panels
                </MenuItem>
              );

            case "view-tags-metadata":
              return (
                <ViewTagsAndMetadata
                  key={key}
                  onClick={handleViewFileDetails}
                />
              );

            case "download-file":
              return fileRecord ? (
                <DownloadFile
                  key={key}
                  fileRecord={fileRecord}
                  onClick={dropdownMenu.close}
                />
              ) : null;

            case "open-in-new-tab":
              return fileRecord ? (
                <OpenFileInNewTab
                  key={key}
                  fileRecord={fileRecord}
                  onClick={dropdownMenu.close}
                />
              ) : null;

            default:
              return null;
          }
        })}
      </Menu>

      <FileDetailsDialog
        open={tagMetadataPopup.isOpen}
        onClose={tagMetadataPopup.close}
        fileRecord={fileRecord}
      />
    </div>
  );
}

type MenuItemKey =
  "clear-panels" | "view-tags-metadata" | "download-file" | "open-in-new-tab";

/**
 * Generates the list of menu item keys to display.
 * @param hasSingleFile - Whether we have a single file record
 * @returns Array of menu item keys to render
 */
function getMenuItemKeys(hasSingleFile: boolean): MenuItemKey[] {
  const keys: MenuItemKey[] = ["clear-panels"];

  if (hasSingleFile) {
    keys.push("view-tags-metadata", "download-file", "open-in-new-tab");
  }

  return keys;
}
